<script setup lang="ts">
import { groupBy, map, filter, pipe, values } from "ramda";
import draggable from 'vuedraggable';
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { z } from "zod";
import { useClaimStore, useNotificationStore } from '@/store';
import { copyToClipboard, getCivilityTitle } from "@/utils";
import { UserMinusIcon, TrashIcon, UserPlusIcon, ArrowRightOnRectangleIcon } from "@heroicons/vue/24/solid";
import ExtractPassengersModal from "./ExtractPassengersModal.vue";
import { format, isValid } from "date-fns";
import { fr } from 'date-fns/locale';
import adminAPI from "@/services/api";
import Ticket from "../reservation/Ticket.vue";
import FullReservationItinerary from "./FullReservationItinerary.vue";
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronUpIcon } from '@heroicons/vue/20/solid'
import { LandPlotIcon } from "lucide-vue-next";
import { WaypointsIcon } from "lucide-vue-next";
import { RouteIcon } from "lucide-vue-next";
import DisplayTicketHistory from "../reservation/DisplayTicketHistory.vue";
import AlertModal from "../ui/modals/AlertModal.vue";
import BaseInput from "../ui/inputs/BaseInput.vue";
import FlightOverview from "../overview/FlightOverview.vue";

const { claim } = storeToRefs(useClaimStore());


const passengersByFamilies = computed(() => {
  const usersClaimsFamilies = groupBy((uc) => uc.familyId, claim.value.usersClaims)
  const mapToTableData = ({user, userSegment, ticketImage, email, ...userClaim }: typeof claim.value.usersClaims[number]) => {
    const civility = `${getCivilityTitle(user.civility)} ${user.firstName} ${user.lastName.toUpperCase()}`
    const emailToShow = email || user.email
    const contact = emailToShow?.endsWith("upclaim.eu") ? null : emailToShow

    const isCodeshare = Boolean(userSegment.segment?.flight?.isCodeshare);

    return {
      id: userSegment.id,
      tickets: ticketImage,
      passenger: {
        id: user.id,
        civility,
        contact,
        birthDate: user.birthdate,
      },
      booking: {
        isCodeshare,
        pnrId: userSegment.segment?.reservation?.gdsPnrId,
        bookingDate: userSegment.segment?.reservation?.pnrCreatedAt
          ? format(new Date(userSegment.segment?.reservation?.pnrCreatedAt), "d MMMM yyyy", { locale: fr, })
          : "(inconnu)",
        flightIdentifier: userSegment.segment?.flight.flightIdentifier,
        airlineName: userSegment.segment?.flight.airline.name,
      },
      familyId: userClaim.familyId,
      familyName: claim.value.usersClaimsFamilies.find(f => f.id === userClaim.familyId)?.familyName,
    }
  };

  const groupedData = map((families) => {
    if (!families) throw new Error("Empty family");

    return families.map(mapToTableData)
  }, usersClaimsFamilies);

  return pipe(values, map(values))(groupedData) as Array<ReturnType<typeof mapToTableData>[]>;

  // return claim.value.usersClaims
});


const allPassengers = computed(() => passengersByFamilies.value.flat());
watch(allPassengers, () => selectedPassengers.value.clear());

const selectedPassengers = ref<Set<string>>(new Set());
const selectedUsersIds = computed(() => Array.from(selectedPassengers.value));
const selectedPassengersData = computed(() => {
  return allPassengers.value.filter(({passenger}) => selectedUsersIds.value.some(sId => sId === passenger.id))
    .map(({ passenger }) => passenger);
})
const addPassengersModalOpen = ref(false);
const extractPassengersModalOpen = ref(false);
const passengersNumber = ref(1);
const passengersToAdd = ref([{ firstName: "", lastName: "" }]);

function toggleSelection(id: string) {
  const isSelected = selectedPassengers.value.has(id);
  if (isSelected) selectedPassengers.value.delete(id);
  else selectedPassengers.value.add(id);
}

function getRowBackgroundColor(index: number, userId: string) {
  if (selectedPassengers.value.has(userId)) return `bg-amber-100 hover:bg-amber-100`;
  else if ((index % 2) !== 0) return 'bg-white'; //`bg-gray-50`;
  return 'bg-white';
};

const submitButton = ref<{ [userSegmentId: string]: boolean }>({});
const usersEmail = ref<{ [userSegmentId: string]: string }>({});

const showSubmit = (userSegmentId: number) => submitButton.value[userSegmentId] = true;
const hideSubmit = (userSegmentId: number) => setTimeout(() => submitButton.value[userSegmentId] = false, 100);

const isValidEmail = (email: string) => Boolean(z.string().email().safeParse(email).success);

async function updatePassenger(userSegmentId: number, data: { email: string }) {
  const { success } = await adminAPI.updatePassenger(claim.value.id, `${userSegmentId}`,
    { email:  data.email });
  if (success) {
    useNotificationStore().emitNotification({ title: "Passager modifié avec succès" });
    useClaimStore().retrieveClaim(claim.value.id);
  }
}

async function softDeletePassengers() {
  const usersClaimsIds = claim.value.usersClaims.filter(uc => selectedPassengers.value.has(uc.userSegment.userId))
    .map(({ id }) => id);
  await adminAPI.softDeletePassengers(claim.value.id, { usersClaimsIds });
  await useClaimStore().refreshClaim();
}

const drag = ref(false);
const transitionOptions = computed(() => { return { animation: 200, disabled: false, ghostClass: "ghost", }; });
</script>
<template>
  <AlertModal
    type="info"
    title="Ajouter un passager"
    message="Ajouter un passager au dossier."
    confirm-message="Ajouter" 
    :open=addPassengersModalOpen
    @close="addPassengersModalOpen = false"
    @confirm="async () => {
      addPassengersModalOpen = false
      await adminAPI.addPassengers(claim.id, {
        users: passengersToAdd
      });
      passengersToAdd = [{ firstName: '', lastName: '' }];
      useNotificationStore().emitNotification({ title: 'Passager ajouté avec succès' });
      useClaimStore().refreshClaim()
    }"
    @open="addPassengersModalOpen = true"
  >
    <div class="divide-y-8 divide-transparent">
      <BaseInput label="Prénom" placeholder="Prénom" v-model="passengersToAdd[0].firstName" />
      <BaseInput label="Nom de famille"  placeholder="Nom de famille" v-model="passengersToAdd[0].lastName" />
    </div>
  </AlertModal>

  <ExtractPassengersModal
    @close="extractPassengersModalOpen = false"
    :users="selectedPassengersData"
    :open="extractPassengersModalOpen"
  />
  
  <div class="px-6 py-5 text-left bg-white">
    <h2 class="text-xl font-medium leading-6 text-gray-900">
      Réservation
    </h2>
    <p class="max-w-2xl mt-1 text-sm font-normal text-gray-500">
      Liste des passagers impactés et leur itinéraire complet.
    </p>
    <div class="mt-4">
      <div class="py-4 border rounded-md bg-gray-50 sm:py-5 sm:grid sm:grid-cols-3">
        <dt class="px-4 text-sm font-medium text-gray-500">
          Réservation
        </dt>
        <dd class="mt-1 text-sm text-gray-700 sm:mt-0 sm:col-span-2">
          <p>PNR : <span class="font-medium" @click="copyToClipboard(claim.legalSpecification.impactedSegment.reservation.id)">{{ claim.legalSpecification.impactedSegment.reservation.gdsPnrId }}</span></p>
          <p>Office : <span class="font-medium">{{ claim.legalSpecification.impactedSegment.reservation.travelAgencyOffice.amadeusOfficeId }}</span> ({{ claim.legalSpecification.impactedSegment.reservation.travelAgencyOffice.name }})</p>
          <p>Agent : <span class="font-medium">{{ claim.legalSpecification.impactedSegment.reservation.travelAgencyAgent?.admin.fullName || '(inconnu)' }}</span>
            <span v-if="claim.legalSpecification.impactedSegment.reservation.travelAgencyAgent">({{ claim.legalSpecification.impactedSegment.reservation.travelAgencyAgent?.admin.email }})</span></p>
          <p v-if="claim.legalSpecification.impactedSegment.reservation.travelAgencyCompany?.displayName">Entreprise : <span class="font-medium">{{ claim.legalSpecification.impactedSegment.reservation.travelAgencyCompany.displayName }}</span></p>
        </dd>
      </div>
    </div>

    <!-- <div class="mt-12">
      <h2 class="text-lg font-medium leading-6 text-gray-900">
        Segment détecté
      </h2>
      <p class="max-w-2xl mt-1 text-sm font-normal text-gray-500">
        E-Tickets pour chaque passager et leurs statuts.
      </p>
      <div class="relative w-full py-2 my-5 border-2 border-indigo-500 rounded-lg bg-indigo-50">
        <div v-if="true" class="flex mt-4 ml-2">
          <span class="px-3 italic text-indigo-700">Segment detecté</span>
        </div>
        <FlightOverview
          :airline="claim.legalSpecification.effectiveAirline"
          :claim-type-name="claim.claimType.name"
          :flight="claim.legalSpecification.effectiveFlight"
          :rerouting-proposals="claim.legalSpecification.impactedSegment.reroutingProposals"
          :actual-rerouting="claim.legalSpecification.impactedSegment.actualRerouting"
        />
      </div>
    </div> -->

    <div class="px-2 mt-12" v-if="!claim.legalSpecification.impactedSegment.reservation.travelAgencyOffice.amadeusOfficeId.endsWith('PASSIVE_OFFICE')">
      <h2 class="text-lg font-medium leading-6 text-gray-900">
        Billets électroniques
      </h2>
      <p class="max-w-2xl mt-1 text-sm font-normal text-gray-500">
        E-Tickets pour chaque passager et leurs statuts.
      </p>
      <div class="w-full py-2">
        <DisplayTicketHistory :claim-id="claim.id" />
      </div>
    </div>

    <div class="-mx-6">
      <div class="w-full py-4">
        <div class="w-full p-2 mx-auto bg-white rounded-2xl">
          <Disclosure :defaultOpen="true" v-slot="{ open }">
            <DisclosureButton
              class="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-left text-blue-900 border border-blue-200 rounded-lg bg-blue-50 hover:bg-blue-100 focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75"
            >
              <span class="inline-flex items-center">
                <span class="mr-3">
                  <!-- <LandPlotIcon /> -->
                  <!-- <WaypointsIcon /> -->
                  <!-- <RouteIcon /> -->
                  <ChevronUpIcon
                :class="open ? 'rotate-180 transform' : ''"
                class="w-5 h-5 text-blue-500"
              />
                </span>
                Réservation détaillée <span class="ml-2 text-xs italic font-normal">(Cliquer pour {{ open ? 'fermer' : 'ouvrir' }})</span>
              </span>
              <!-- <RouteIcon /> -->
                  <LandPlotIcon />

            </DisclosureButton>
            <DisclosurePanel class="pt-4 pb-2 text-sm text-gray-500">
                <FullReservationItinerary
                  :reservation-id="claim.legalSpecification.impactedSegment.reservationId"
                  :impactedSegmentId="claim.legalSpecification.impactedSegmentId"
                  :claim-type="claim.claimType.name"
                />
            </DisclosurePanel>
          </Disclosure>
        </div>
      </div>
    </div>

  </div>
  <div class="flex flex-col">
    <div class="-my-2 overflow-x-auto lg:-mx-8">
      <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
        <div class="">
          <table v-for="passengerFamily of passengersByFamilies" class="min-w-full my-3 overflow-hidden border-gray-200 divide-y divide-gray-200 shadow border-y sm:rounded-lg">
            <thead class="bg-stone-100">
              <tr>
                <th scope="col" class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  {{ passengerFamily.length === 1 ? 'Passager' : `Famille ${passengerFamily[0].familyName}`  }}
                </th>
                <th scope="col" class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Réservation
                </th>
                <th scope="col" class="relative px-6 py-3">
                  <span class="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
              <tbody class="bg-white divide-y divide-gray-200 border-t-1">
                <template v-for="(element, index) in passengerFamily" :key="element.id">
                  <tr :class="getRowBackgroundColor(index, element.passenger.id)" @click="toggleSelection(element.passenger.id)" class="cursor-pointer hover:bg-gray-100">
                    <td class="py-4 whitespace-nowrap">
                      <div class="flex items-center text-left">
                        <div class="ml-6">
                          <div class="text-sm font-medium text-gray-900">
                            {{ element.passenger.civility }}
                          </div>
                          <div class="text-sm text-gray-500">
                            <div v-if="element.passenger.contact" @click.stop="copyToClipboard(element.passenger.id)">{{ element.passenger.contact }}</div>
                            <div v-else @click.stop="showSubmit(element.id)">
                              <!-- <label for="email" class="block text-sm font-medium leading-6 text-gray-900">Email</label> -->
                              <!-- <p>{{ usersEmail[element.id] }}</p> -->
                              <div class="mt-2">
                                <input v-model="usersEmail[element.id]" @focusout="hideSubmit(element.id)" autocomplete="false" type="text" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                  placeholder="Email non renseigné" />
                              </div>
                            </div>
                            <div @click.stop="updatePassenger(element.id, { email: usersEmail[element.id] })" class="mt-2">
                                <button type="button"
                                  v-show="submitButton[element.id] && isValidEmail(usersEmail[element.id])"
                                  class="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                  Enregistrer
                                </button>
                              </div>
                          </div>
                          <div class="text-sm text-gray-500" v-if="isValid(element.passenger.birthDate)">
                            Né le: {{ new Date(element.passenger.birthDate).toLocaleDateString() }}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 text-left whitespace-nowrap">
                      <div class="text-sm text-gray-900">{{ element.booking.flightIdentifier }}</div>
                      <div class="text-sm text-gray-500">
                        {{ element.booking.airlineName }}
                      </div>
                      <div>
                        Date de réservation : {{ element.booking.bookingDate }}
                      </div>
                      <div>
                        <span v-if="element.booking.isCodeshare" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                          <svg class="mr-1.5 h-2 w-2 text-indigo-400" fill="currentColor" viewBox="0 0 8 8">
                            <circle cx="4" cy="4" r="3" />
                          </svg>
                          Codeshare
                        </span>
                        <span v-else class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Compagnie effective
                        </span>
                      </div>
                    </td>
                    <td class="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                      <input :checked="selectedPassengers.has(element.passenger.id)" name="select-passenger" type="checkbox" class="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500" />
                    </td>
                  </tr>
                  <tr v-if="element.tickets?.beforeCancellationImage && false">
                    <td :colspan="3" class="p-5">
                      <div class="mb-6">
                        <h3 class="p-2 text-sm font-medium text-left text-slate-800">Image du billet, juste avant l'annulation :</h3>
                        <Ticket :ticket="(element.tickets.beforeCancellationImage as any)" />
                      </div>
                      <!-- <div>
                        <h3 class="p-2 text-base font-medium text-left text-slate-800">Dernière image :</h3>
                        <Ticket :ticket="(data.tickets.lastImage as any)?.ticket" />  
                      </div> -->
                    </td>
                  </tr>
                </template>
              </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <div v-if="selectedPassengers.size > 0" v-can="['UPCLAIM_ADMIN']" class="flex justify-end flex-1 px-6 mt-6">
    <button
      @click="extractPassengersModalOpen = true"
      class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-bold text-white bg-indigo-600 border border-indigo-300 rounded-md cursor-pointer hover:bg-indigo-700"
    >
        <ArrowRightOnRectangleIcon class="-ml-0.5 mr-2 h-5 w-5" aria-hidden="true" />
        Extraire {{ selectedPassengers.size }} passager(s)
    </button>
    <button
      @click="softDeletePassengers"
      class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-bold text-white bg-red-600 border border-red-300 rounded-md cursor-pointer hover:bg-red-700"
    >
        <UserMinusIcon class="-ml-0.5 mr-2 h-5 w-5" aria-hidden="true" />
        Supprimer {{ selectedPassengers.size }} passager(s)
    </button>
  </div>
  <div v-else class="flex justify-end flex-1 px-6 mt-6">
    <button
      @click="addPassengersModalOpen = true"
      class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-bold text-white bg-blue-600 border border-blue-300 rounded-md cursor-pointer hover:bg-blue-700"
    >
        <UserPlusIcon class="-ml-0.5 mr-2 h-5 w-5" aria-hidden="true" />
        Ajouter un passager
    </button>
  </div>
</template>
